$(document).ready(() => {
  // ========= GSAP + Lenis Init =========
  const initGsapAndLenis = () => {
    gsap.registerPlugin(ScrollTrigger);

    const lenis = new Lenis();
    lenis.on("scroll", () => ScrollTrigger.update());
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(200);

    $(window).scrollTop(0);
    ScrollTrigger.refresh();
  };

  // ====HELPER AND COMMON FUN=====
  // ========= Common Parallax Utility (Reusable) =========
  const ParallaxUtil = (() => {
    const defaults = {
      selector: ".parallaxObject", // what to parallax inside root
      maxMove: 45, // strength
      ease: 0.22, // smoothness
      enableRotate: true,
      rotateMax: 4, // max rotate degrees
      depthStart: 0.35,
      depthStep: 0.18,
      disableOnTouch: true,
      resetOnLeave: true,
    };

    const init = (root, opts = {}) => {
      if (!root) return;
      const o = { ...defaults, ...opts };

      const els = root.querySelectorAll(o.selector);
      if (!els.length) return;

      if (o.disableOnTouch) {
        const isTouch = window.matchMedia("(pointer: coarse)").matches;
        if (isTouch) return;
      }

      // smoother than gsap.to per mousemove
      const setters = Array.from(els).map((el, idx) => {
        const depth = o.depthStart + idx * o.depthStep;
        return {
          el,
          depth,
          xTo: gsap.quickTo(el, "x", { duration: o.ease, ease: "power3.out" }),
          yTo: gsap.quickTo(el, "y", { duration: o.ease, ease: "power3.out" }),
          rTo: o.enableRotate
            ? gsap.quickTo(el, "rotation", {
                duration: o.ease,
                ease: "power3.out",
              })
            : null,
        };
      });

      const onMove = (e) => {
        const rect = root.getBoundingClientRect();
        const relX = (e.clientX - rect.left) / rect.width;
        const relY = (e.clientY - rect.top) / rect.height;

        const x = (relX - 0.5) * 2; // -1..1
        const y = (relY - 0.5) * 2; // -1..1

        setters.forEach((s) => {
          const move = o.maxMove * s.depth;
          s.xTo(x * move);
          s.yTo(y * move);

          if (s.rTo) s.rTo(x * o.rotateMax * s.depth);
        });
      };

      root.addEventListener("mousemove", onMove);

      if (o.resetOnLeave) {
        root.addEventListener("mouseleave", () => {
          setters.forEach((s) => {
            s.xTo(0);
            s.yTo(0);
            if (s.rTo) s.rTo(0);
          });
        });
      }
    };

    return { init };
  })();

  // ========= Theme HERO ANIMATION  =========
  const ThemeHeroAnimations = (() => {
    // =========================
    //  CONFIG (easy toggles)
    // =========================
    const CONFIG = {
      enableFloatingScrollScale: false, //  enable/disable scroll scale for floating items
      floatingScrollScaleFrom: 1,
      floatingScrollScaleTo: 1.07,

      // floating movement amounts (LOWER = less height)
      floatingAmpX: 10,
      floatingAmpY: 14,
      floatingDurBase: 4.5,

      // parallax strength
      parallaxMaxMove: 45, // increase for stronger effect
      parallaxEase: 0.22, // smaller = snappier, bigger = smoother (0.18–0.35 good range)
    };

    // ========= Helper: Floating (infinite smooth) =========
    // IMPORTANT: no rotation changes so your CSS rotate stays intact.
    const initFloatingItems = (root) => {
      const imgs = root.querySelectorAll(".floatingImgsContainer .boxImg");
      if (!imgs.length) return;

      imgs.forEach((img, i) => {
        const dir = i % 2 === 0 ? 1 : -1;

        // lower movement (less height)
        const ampX = CONFIG.floatingAmpX + i * 2;
        const ampY = CONFIG.floatingAmpY + i * 3;
        const dur = CONFIG.floatingDurBase + i * 0.35;

        const tl = gsap.timeline({
          repeat: -1,
          yoyo: true,
          defaults: { ease: "sine.inOut" },
        });

        tl.to(img, {
          duration: dur,
          x: ampX * dir,
          y: -ampY,
        }).to(img, {
          duration: dur * 0.85,
          x: -ampX * dir,
          y: ampY * 0.7,
        });

        // offset so they aren't synced
        tl.progress((i * 0.27) % 1);
      });

      // ========= Optional: Scroll scale for floating items =========
      if (CONFIG.enableFloatingScrollScale) {
        imgs.forEach((img, i) => {
          const dir = i % 2 === 0 ? 1 : -1;

          gsap.to(img, {
            scale: CONFIG.floatingScrollScaleTo,
            ease: "none",
            scrollTrigger: {
              trigger: root,
              start: "top bottom",
              end: "bottom top",
              scrub: 1.2,
              // subtle variation per image (optional)
              onUpdate: (self) => {
                const t = self.progress; // 0..1
                const scale =
                  CONFIG.floatingScrollScaleFrom +
                  (CONFIG.floatingScrollScaleTo -
                    CONFIG.floatingScrollScaleFrom) *
                    t;

                // tiny scale breathing difference per image
                gsap.set(img, { scale: scale + 0.008 * dir });
              },
            },
          });
        });
      }
    };

    // ========= Helper: Scroll “popcorn” wobble (smooth) =========
    const initScrollPopcorn = (root) => {
      const objs = root.querySelectorAll(".parallaxObject");
      if (!objs.length) return;

      objs.forEach((el, idx) => {
        const dir = idx % 2 === 0 ? 1 : -1;

        gsap.to(el, {
          y: 28 * dir,
          rotation: 5 * dir,
          ease: "none",
          scrollTrigger: {
            trigger: root,
            start: "top bottom",
            end: "bottom top",
            scrub: 1.5,
          },
        });

        // soft pop in
        gsap.fromTo(
          el,
          { scale: 0.92, autoAlpha: 0 },
          {
            scale: 1,
            autoAlpha: 1,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: root,
              start: "top 80%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });
    };

    // ========= 2) Section Scoped Init =========
    const initImageOverlayFloatingSections = () => {
      const sections = document.querySelectorAll(
        ".imageWithTextOverlaySec.animatedFloatingItemsBannerSec"
      );
      if (!sections.length) return;

      sections.forEach((root) => {
        gsap.context(() => {
          initFloatingItems(root);
          initScrollPopcorn(root);
          ParallaxUtil.init(root, {
            selector: ".parallaxObject",
            maxMove: CONFIG.parallaxMaxMove,
            ease: CONFIG.parallaxEase,
            enableRotate: true,
            rotateMax: 3,
          });
        }, root);
      });
    };

    // ========= 3) Public Init =========
    const init = () => {
      initImageOverlayFloatingSections();
    };

    return { init };
  })();

  // ========= Image With Text: Scroll Loop + Swap =========
  const ImgWithTxtScrollLoopObjects = (() => {
    const CONFIG = {
      speed: 900, //make sure use 600 to 1300
      depthStep: 0.1, // 0.06–0.18

      wrapPadding: 160, // px

      enableSwapOnWrap: true,
      enableFadeOnSwap: false,
      swapFadeDuration: 0.22,

      // optional scale breathing based on scroll (enable if you want)
      enableScrollScale: false,
      scrollScaleFrom: 1,
      scrollScaleTo: 1.08,
    };

    const setWillChange = (root) => {
      const els = root.querySelectorAll(
        ".animatedImgTxtBgObject .animatedObjContainer"
      );
      if (!els.length) return;
      els.forEach((el) => (el.style.willChange = "transform"));
    };

    const initSection = (root) => {
      const containers = Array.from(
        root.querySelectorAll(".animatedImgTxtBgObject .animatedObjContainer")
      );
      if (!containers.length) return;

      // pool of images (we'll cycle them)
      const srcPool = containers
        .map((c) => c.querySelector("img"))
        .filter(Boolean)
        .map((img) => img.getAttribute("src"))
        .filter(Boolean);

      // store state per object
      const state = containers.map((el, i) => {
        const img = el.querySelector("img");
        return {
          el,
          img,
          y: 0,
          // each object has its own depth multiplier
          depth: 0.7 + i * CONFIG.depthStep,
          // start with its own src index (or 0)
          srcIndex: i % Math.max(1, srcPool.length),
        };
      });

      // size-based wrap boundaries (recalculated on refresh)
      let wrapMin = 0;
      let wrapMax = 0;

      const recalcWrap = () => {
        const h = root.offsetHeight || 600;
        // objects will wrap between these y values
        wrapMin = -(h / 2) - CONFIG.wrapPadding;
        wrapMax = +(h / 2) + CONFIG.wrapPadding;
      };

      recalcWrap();
      ScrollTrigger.addEventListener("refreshInit", recalcWrap);

      // helper: swap image (cycles through pool)
      const swapToNextImage = (s) => {
        if (!CONFIG.enableSwapOnWrap || !srcPool.length || !s.img) return;

        s.srcIndex = (s.srcIndex + 1) % srcPool.length;
        const nextSrc = srcPool[s.srcIndex];

        if (!CONFIG.enableFadeOnSwap) {
          s.img.src = nextSrc;
          return;
        }

        gsap.to(s.img, {
          autoAlpha: 0,
          duration: CONFIG.swapFadeDuration,
          ease: "power1.out",
          overwrite: "auto",
          onComplete: () => {
            s.img.src = nextSrc;
            gsap.to(s.img, {
              autoAlpha: 1,
              duration: CONFIG.swapFadeDuration,
              ease: "power1.out",
              overwrite: "auto",
            });
          },
        });
      };

      // init starting y so they aren't all aligned
      state.forEach((s, i) => {
        s.y = gsap.utils.random(wrapMin * 0.35, wrapMax * 0.35);
        gsap.set(s.el, { y: s.y });
      });

      // optional scale on scroll
      if (CONFIG.enableScrollScale) {
        state.forEach((s, i) => {
          gsap.fromTo(
            s.el,
            { scale: CONFIG.scrollScaleFrom },
            {
              scale: CONFIG.scrollScaleTo,
              ease: "none",
              scrollTrigger: {
                trigger: root,
                start: "top bottom",
                end: "bottom top",
                scrub: 1.4,
              },
            }
          );
        });
      }

      // scroll-driven looping
      let lastProgress = 0;

      ScrollTrigger.create({
        trigger: root,
        start: "top bottom",
        end: "bottom top",
        scrub: true,
        onUpdate: (self) => {
          // progress delta tells direction: + = scrolling down
          const dp = self.progress - lastProgress;
          lastProgress = self.progress;

          // convert progress delta to pixels
          // scrolling down => objects go UP (negative y)
          const baseDeltaY = -(dp * CONFIG.speed);

          state.forEach((s) => {
            const prevY = s.y;
            s.y += baseDeltaY * s.depth;

            // wrap + swap when crossing boundaries
            if (s.y < wrapMin) {
              // moved too far up -> jump to bottom
              s.y = wrapMax;
              if (prevY >= wrapMin) swapToNextImage(s);
            } else if (s.y > wrapMax) {
              // moved too far down -> jump to top
              s.y = wrapMin;
              if (prevY <= wrapMax) swapToNextImage(s);
            }

            gsap.set(s.el, { y: s.y });
          });
        },
      });
    };

    const init = () => {
      const sections = document.querySelectorAll(
        ".imgWithTxtSec.hasAnimatedObj"
      );
      if (!sections.length) return;

      sections.forEach((root) => {
        gsap.context(() => {
          setWillChange(root);
          initSection(root);

          ParallaxUtil.init(root, {
            selector: ".hasBgContainer .image-element__wrap",
            maxMove: 30,
            ease: 1,
            enableRotate: false,
            resetOnLeave: true,
          });
          ParallaxUtil.init(root, {
            selector: ".bgContainer",
            maxMove: 80,
            ease: 0.5,
            enableRotate: false,
            resetOnLeave: true,
          });
        }, root);
      });
    };

    return { init };
  })();

  // === Floating In Air Objects: Simple Infinite Float (no scroll) ===
  const initFloatingInAirOrbit = (root) => {
    const wrap = root.querySelector(".floatingInAirObject");
    if (!wrap) return;

    const items = Array.from(wrap.querySelectorAll(".floatingObjContainer"));
    if (!items.length) return;

    // optional: skip on touch devices
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    if (isTouch) return;

    // prevent layout jank
    items.forEach((el) => (el.style.willChange = "transform"));

    // Per-item orbit state
    const states = items.map((el, i) => {
      const baseR = gsap.utils.random(16, 42); // orbit radius
      const dir = i % 2 === 0 ? 1 : -1; // alternate direction
      const speed = gsap.utils.random(0.85, 1.5) * dir; // angular speed
      const offset = gsap.utils.random(0, Math.PI * 2); // start angle
      const tilt = gsap.utils.random(-6, 6); // subtle rotation wobble

      return {
        el,
        r: baseR,
        speed,
        tilt,
        a: offset,
      };
    });

    // Main orbit loop
    const tick = () => {
      states.forEach((s) => {
        // advance angle
        s.a += 0.013 * s.speed;

        // oval orbit
        const x = Math.cos(s.a) * s.r;
        const y = Math.sin(s.a) * (s.r * 0.72);

        gsap.set(s.el, {
          x,
          y,
          rotation: s.tilt,
          force3D: true,
        });
      });
    };

    gsap.ticker.add(tick);

    // optional cleanup pattern
    // return () => gsap.ticker.remove(tick);
  };
  // ===Popcorn Bowl and Arrow Section ====
  const imgParallaxSec = () => {
    const sections = document.querySelectorAll(".hasParallaxImg");

    if (!sections.length) return;

    sections.forEach((root) => {
      ParallaxUtil.init(root, {
        selector: ".parallaxImgBox img",
        maxMove: 100,
        ease: 1,
        enableRotate: false,
        resetOnLeave: true,
      });
    });
  };

  const floatingObjects = () => {
    const sections = document.querySelectorAll(".hasFLoatingObjects");
    if (!sections.length) return;
    sections.forEach((root) => {
      initFloatingInAirOrbit(root);
    });
  };

  // =====AJAX ADD TO CART=====
  const quickATC = () => {
    $(document).on("click", ".quickATCBtn", function () {
      const $this = $(this);
      const $variantId = $this.attr("data-variantId");
      const $thisBtnHtml = $this.html();
      $this
        .empty()
        .html(`<span class="button is-loading is-loading--icon-only"></span>`)
        .addClass("focused");
      $.ajax({
        type: "POST",
        url: "/cart/add.js",
        data: { quantity: 1, id: $variantId },
        dataType: "json",
        success: function () {
          Shopify.theme.jsAjaxCart.updateView();
          $this.empty().html(`Added`).removeClass("focused");
          setTimeout(() => {
            $this.empty().html($thisBtnHtml);
            Shopify.theme.jsAjaxCart.showDrawer();
          }, 1600);
        },
      });
    });
  };

  // =====SUBSCRIPTION WIDGET=====
  const subscriptionWidget = () => {
    $(document).on("change", ".js-purchase-option", function () {
      const $widget = $(this).closest("[data-subscription-widget]");
      const selectedVal = $(this).val();
      const $plansWrap = $widget.find(".js-subscription-plans");
      const $planSelect = $widget.find(".js-selling-plan-select");
      const $planInput = $widget.find(".js-selling-plan-input");

      if (selectedVal === "subscription") {
        $plansWrap.slideToggle(400);
        $planInput.val($planSelect.val());
      } else {
        $plansWrap.slideToggle(400);
        $planInput.val("");
      }
    });

    $(document).on("change", ".js-selling-plan-select", function () {
      const $widget = $(this).closest("[data-subscription-widget]");
      const $planInput = $widget.find(".js-selling-plan-input");
      $planInput.val($(this).val());
    });
  };

  // ==== STICKY SIDE IMAGE AND TEXT (MULTI SECTION) =====
  const stikcySideAnimation = () => {
    if ($(".stickyImgTxtWrapper").length > 0) {
      if (window.innerWidth >= 798) {
        const SETTINGS = {
          holdVH: 0.1,
          bottomImgVH: 0.1,
          markers: false,
        };

        const $sections = $(".stickyImgTxtWrapper");
        if (!$sections.length) return;

        const vh = () => window.innerHeight;

        $sections.each(function () {
          const section = this;

          const wrappers = Array.from(
            section.querySelectorAll(
              ".sideStickyContentSection .stickyTxtCol .image-with-text__wrapper"
            )
          );

          if (!wrappers.length) return;

          const bottomImg = section.querySelector(".fullSizeBottomImg");

          // initial text state
          gsap.set(wrappers, { autoAlpha: 0, y: vh() * 0.6 });

          if (bottomImg) gsap.set(bottomImg, { y: 0 });

          const totalVH =
            SETTINGS.holdVH +
            wrappers.length +
            (bottomImg ? SETTINGS.bottomImgVH : 0);

          const totalScroll = () => totalVH * vh();

          const tl = gsap.timeline({
            defaults: { ease: "none" },
            scrollTrigger: {
              trigger: section,
              start: "top top",
              end: () => `+=${totalScroll()}`,
              scrub: true,
              pin: true,
              pinSpacing: true,
              invalidateOnRefresh: true,
              markers: SETTINGS.markers,
            },
          });

          wrappers.forEach((wrap, i) => {
            tl.to(
              wrap,
              { autoAlpha: 1, y: 0, duration: 1 },
              SETTINGS.holdVH + i
            );
          });

          if (bottomImg) {
            const bottomStart = SETTINGS.holdVH + wrappers.length;

            tl.to(bottomImg, { y: -vh(), duration: 1 }, bottomStart);
          }
        });

        ScrollTrigger.refresh();
        setTimeout(() => ScrollTrigger.refresh(), 300);
      } else {
        (() => {
          // Make sure GSAP + ScrollTrigger are loaded
          gsap.registerPlugin(ScrollTrigger);

          const SELECTOR_SECTION = ".sideStickyContentSection";
          const SELECTOR_CARD = ".stickyTxtCol";
          const SELECTOR_FOOTER = "footer, .footer .footer__container";

          const HEADER_OFFSET = 80; // px
          const PIN_HEIGHT_RATIO = 0.8; // 0..1 (80% of viewport)
          const INTRO_DELAY = 400; // px of scroll before revealing card #2+
          const GAP = 24; // px between stacked cards
          const PER_CARD_SCROLL_VH = 0.7; // 1.2 = 120vh per reveal segment
          const HOLD_SCROLL_VH = 1.5; // extra pin hold after all cards land
          const END_BUFFER = 200; // px safety buffer
          const START_BELOW_BUFFER = 120; // px below stage to start cards

          const footer = document.querySelector(SELECTOR_FOOTER);

          const getPageY = (el) =>
            el.getBoundingClientRect().top + window.scrollY;

          const getPinnedViewport = () =>
            Math.max(0, window.innerHeight * PIN_HEIGHT_RATIO - HEADER_OFFSET);

          function getFooterPinDistance(sectionEl) {
            if (!footer) return 0;

            const sectionTop = getPageY(sectionEl);
            const footerTop = getPageY(footer);

            return Math.max(0, footerTop - sectionTop - getPinnedViewport());
          }

          function initStickyReveal(sectionEl) {
            const cards = gsap.utils.toArray(
              sectionEl.querySelectorAll(SELECTOR_CARD)
            );
            if (!cards.length) return;

            let tl;

            const build = () => {
              if (tl) tl.kill();

              // Set pinned "stage" height
              gsap.set(sectionEl, { height: getPinnedViewport() });

              const stageH = sectionEl.clientHeight;
              const startBelow = stageH + START_BELOW_BUFFER;

              // Compute dynamic slots from actual heights
              const slots = [];
              let acc = 0;
              cards.forEach((el) => {
                slots.push(acc);
                acc += el.offsetHeight + GAP;
              });

              // Start all cards below
              gsap.set(cards, { y: startBelow, opacity: 1 });

              // ✅ First card is immediately "stuck" at top when pin starts
              gsap.set(cards[0], { y: slots[0] });

              // Scroll math
              const perCardScroll = window.innerHeight * PER_CARD_SCROLL_VH;
              const revealCount = Math.max(0, cards.length - 1); // we animate card #2+
              const revealScroll = perCardScroll * revealCount;
              const holdScroll = window.innerHeight * HOLD_SCROLL_VH;

              const endDistance =
                Math.max(
                  revealScroll + holdScroll + INTRO_DELAY,
                  getFooterPinDistance(sectionEl)
                ) + END_BUFFER;

              // px delay -> timeline units (each reveal tween has duration: 1)
              const delayUnits = INTRO_DELAY / perCardScroll;

              tl = gsap.timeline({
                scrollTrigger: {
                  trigger: sectionEl,
                  start: `top ${HEADER_OFFSET}px`,
                  end: () => `+=${endDistance}`,
                  pin: true,
                  pinSpacing: true,
                  scrub: true,
                  anticipatePin: 1,
                  invalidateOnRefresh: true,
                  pinReparent: true,
                  markers: false,
                },
              });

              // Reveal cards #2..n into their slots, one-by-one (after intro delay)
              cards.forEach((el, i) => {
                if (i === 0) return;
                tl.to(
                  el,
                  {
                    y: slots[i],
                    ease: "none",
                    duration: 1,
                    immediateRender: false,
                  },
                  delayUnits + (i - 1)
                );
              });

              // Hold after last card lands (so section remains pinned)
              tl.to({}, { duration: 1 }, delayUnits + revealCount);

              ScrollTrigger.refresh();
            };

            build();

            // Rebuild on resize and after load (images/fonts can change heights)
            window.addEventListener("resize", build);
            window.addEventListener("load", () => {
              build();
              ScrollTrigger.refresh();
            });
          }

          // Init all matching sections on the page
          gsap.utils.toArray(SELECTOR_SECTION).forEach(initStickyReveal);
        })();
      }
    }
  };

  // =====MOBILEHEADER AND MENU=======
  const mobileHeader = () => {
    const headerHeight = $(".headerContainer.sticky-true").height();
    $(".headerContainer.sticky-true").css("height", headerHeight);

    $(window).scroll(function () {
      if ($(this).scrollTop() > 42) {
        $(".headerContainer.sticky-true").addClass("scrolled");
      } else {
        $(".headerContainer.sticky-true").removeClass("scrolled");
      }
    });

    var hasOverlayHeader =
      $(".dynamic-sections").children().length > 0 &&
      $(".dynamic-sections")
        .children()
        .first()
        .hasClass("overlaid-header-option");

    if (hasOverlayHeader) {
      $("body").addClass("isOverlayHeader");
    } else {
      $("body").removeClass("isOverlayHeader");
    }

    $(document).on("click", ".menuBtn", function () {
      $("html, body").toggleClass("hideScroll");
      $(".mobile-header").toggleClass("showMenu");
      if ($(".mobile-header").hasClass("showMenu")) {
        $(".header-section").attr("data-lenis-prevent", "true");
      } else {
        $(".header-section").removeAttr("data-lenis-prevent");
      }
    });

    $(document).on("click", ".backToFirstLevel", function () {
      $(".submenu__input:checked").prop("checked", false);
    });
  };

  ///  ========= MOBILE SLIDER INIT =========
  const enableMobileSlider = () => {
    if ($(".enableMobileSlider-true").length > 0) {
      $(".enableMobileSlider-true").each(function () {
        new Flickity(this, {
          lazyLoad: 2,
          freeScroll: false,
          imagesLoaded: true,
          draggable: true,
          cellAlign: "left",
          wrapAround: true,
          pageDots: false,
          contain: true,
          prevNextButtons: false,
          initialIndex: 0,
          fade: false,
          watchCSS: true,
        });
      });
    }
  };

  // ===== MOUSE FOLLOWER =====

  const mouseFollower = () => {
    if ($(".hasHoverImg").length > 0) {
      const $sections = $(".hasHoverImg");

      $sections.each(function () { 
        const $section = $(this);

        // Prevent double init (Shopify editor / re-render)

        const $follower = $section.find("[data-follower]");
        const $hoverArea = $section.find("[data-hover-area]").addBack("[data-hover-area]");
        const hasFollower =
          $follower.length &&
          $section.hasClass("hasHoverImg") &&
          $hoverArea.length;

        if (hasFollower) {
          const followerEl = $follower[0];

          // Center follower on cursor
          gsap.set(followerEl, {
            opacity: 0,
            scale: 0.92,
            xPercent: -50,
            yPercent: -50,
            force3D: true,
          });

          // "Butter" follow with delay + elastic-ish ease
          // Increase duration for more delay. Use ease for "buttery" movement.
          const xTo = gsap.quickTo(followerEl, "x", {
            duration: 0.55,
            ease: "elastic.out(1, 0.35)",
            overwrite: "auto",
          });

          const yTo = gsap.quickTo(followerEl, "y", {
            duration: 0.55,
            ease: "elastic.out(1, 0.35)",
            overwrite: "auto",
          });

          let isActive = false;

          const moveFollower = (e) => {
            if (!isActive) return;

            const rect = $section[0].getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            // IMPORTANT: only move here (no opacity)
            xTo(x);
            yTo(y);
          };

          $hoverArea.on("mouseenter", function (e) {
            isActive = true;

            // Set immediate position so it doesn't "jump in" from old coords
            const rect = $section[0].getBoundingClientRect();
            xTo(e.clientX - rect.left);
            yTo(e.clientY - rect.top);

            gsap.killTweensOf(followerEl, "opacity,scale");
            gsap.to(followerEl, {
              opacity: 1,
              scale: 1,
              duration: 0.22,
              ease: "power2.out",
              overwrite: "auto",
            });
          });

          $hoverArea.on("mouseleave", function () {
            isActive = false;

            // Kill any opacity/scale tweens and fade out
            gsap.killTweensOf(followerEl, "opacity,scale");
            gsap.to(followerEl, {
              opacity: 0,
              scale: 0.92,
              duration: 0.25,
              ease: "power2.inOut",
              overwrite: "auto",
            });
          });

          // Use hoverArea mousemove (not whole section) so it stops reliably
          $hoverArea.on("mousemove", moveFollower);
        }

        // ---------- Reveal text ----------
        // ---------- Simple Reveal (one click event on section) ----------
        $(document).off("click.simpleReveal").on("click.simpleReveal", ".headingSectionArea", function (e) {
            const $section = $(this);

            // OPTIONAL safety: ignore clicks on links that should navigate
            if ($(e.target).closest("a[href]:not([data-reveal-btn])").length)
              return;

            const $btn = $section.find("[data-reveal-btn]").first();
            const $reveal = $section.find("[data-reveal]").first();

            if (!$btn.length || !$reveal.length) return;

            const labelOpen = $btn.attr("data-label-open") || $btn.text();
            const labelClose = $btn.attr("data-label-close") || "Hide";

            const isOpen = $section.data("reveal-open") === true;
            const nextOpen = !isOpen;

            $section.data("reveal-open", nextOpen);

            if (nextOpen) {
              $btn.text(labelClose);
              $reveal.show();

              gsap.fromTo(
                $reveal[0],
                { height: 0, opacity: 0 },
                {
                  height: "auto",
                  opacity: 1,
                  duration: 0.35,
                  ease: "power2.out",
                  overwrite: "auto",
                }
              );
            } else {
              $btn.text(labelOpen);

              gsap.to($reveal[0], {
                height: 0,
                opacity: 0,
                duration: 0.28,
                ease: "power2.inOut",
                overwrite: "auto",
                onComplete: () => $reveal.hide(),
              });
            }
          });
      });
    }
  };

  // ====CALLING AFTER PAGE LOAD=====
  initGsapAndLenis();
  quickATC();
  subscriptionWidget();
  ThemeHeroAnimations.init();
  ImgWithTxtScrollLoopObjects.init();
  imgParallaxSec();
  floatingObjects();
  stikcySideAnimation();
  mobileHeader();
  enableMobileSlider();
  mouseFollower();
});
